// src/pages/patient/dashboardSections.tsx
//
// The patient dashboard's section list — key, label, icon, scroll anchor and visibility.
//
// ⚠️ **THIS IS THE SINGLE SOURCE OF TRUTH AND IT HAS TWO CONSUMERS THAT MUST NOT DRIFT.**
// `Dashboard.tsx` renders the sections; `Sidebar.tsx` renders the nav entries that scroll to
// them. Before this module the list was duplicated four times inside `Dashboard.tsx` (the KPI
// tile array, the section JSX, the `tourSteps` array, and the `isPeptideOnly` /
// `isAppointmentAvailable` gate repeated at each). Adding the sidebar as a fifth copy would
// have guaranteed a section that appears in the nav and not on the page, or the reverse.
//
// `.tsx` rather than `.ts` because the icons are JSX elements.
//
// This module deliberately depends on NOTHING but React and @ant-design/icons — no
// `hasFeature()`, no `ENV`, no import from `Dashboard.tsx`. `visiblePatientSections()` takes
// both gates as arguments so the sidebar and the page provably pass the same values, and so
// the function stays pure and trivially testable.

import React from "react";
import {
	CalendarOutlined,
	ExperimentOutlined,
	FileOutlined,
	FileTextOutlined,
	FormOutlined,
	LinkOutlined,
	MedicineBoxOutlined,
	SolutionOutlined,
} from "@ant-design/icons";

export type PatientSectionKey =
	| "supplements"
	| "forms"
	| "reports"
	| "links"
	| "resources"
	| "results"
	| "medications"
	| "appointments";

export interface PatientDashboardSection {
	key: PatientSectionKey;
	label: string;
	icon: React.ReactNode;
	/** DOM id on the section wrapper; also the scroll target. */
	domId: string;
	/** data-testid on the sidebar nav entry. */
	navTestId: string;
}

/**
 * The 8 sections, in the DOM order `Dashboard.tsx` renders them.
 *
 * ⚠️ **DO NOT REORDER (decision D5 on ClickUp 86d406193).** Rail order equals scroll order, so
 * the scroll-spy highlight moves straight down the rail as the patient scrolls instead of
 * appearing to jump between non-adjacent entries. A patient-priority order (Forms and Test
 * Results first) was considered and rejected — it is a page reflow nobody asked for.
 *
 * The icons are the ones `Dashboard.tsx` already used for each section; the registry moves
 * them, it does not choose new ones.
 */
export const PATIENT_DASHBOARD_SECTIONS: PatientDashboardSection[] = [
	{ key: "supplements", label: "Supplements", icon: <MedicineBoxOutlined />, domId: "patient-section-supplements", navTestId: "patient-portal-nav-supplements" },
	{ key: "forms", label: "Forms", icon: <FormOutlined />, domId: "patient-section-forms", navTestId: "patient-portal-nav-forms" },
	{ key: "reports", label: "Reports", icon: <FileTextOutlined />, domId: "patient-section-reports", navTestId: "patient-portal-nav-reports" },
	// "Helpful Links", not "Handy Links". The two strings both shipped — the deleted KPI tile
	// said one and the section header said the other. This is now the only place the label
	// lives, and "Helpful Links" wins because it matches the Tour copy.
	{ key: "links", label: "Helpful Links", icon: <LinkOutlined />, domId: "patient-section-links", navTestId: "patient-portal-nav-links" },
	{ key: "resources", label: "Resources", icon: <FileOutlined />, domId: "patient-section-resources", navTestId: "patient-portal-nav-resources" },
	{ key: "results", label: "Test Results", icon: <ExperimentOutlined />, domId: "patient-section-results", navTestId: "patient-portal-nav-results" },
	{ key: "medications", label: "Medications", icon: <SolutionOutlined />, domId: "patient-section-medications", navTestId: "patient-portal-nav-medications" },
	{ key: "appointments", label: "Appointments", icon: <CalendarOutlined />, domId: "patient-section-appointments", navTestId: "patient-portal-nav-appointments" },
];

/**
 * The same sections, by key.
 *
 * Exists so a consumer that needs ONE section's `domId` or `label` — the dashboard's section
 * wrappers and its Tour steps — does not re-derive `patient-section-${key}` by hand. That
 * re-derivation is precisely how the anchor and the nav entry drift apart.
 */
export const PATIENT_SECTION_BY_KEY = Object.fromEntries(
	PATIENT_DASHBOARD_SECTIONS.map((section) => [section.key, section]),
) as Record<PatientSectionKey, PatientDashboardSection>;

/**
 * The sections a given patient can see.
 *
 * ⚠️ **THIS IS THE ONLY PLACE THESE TWO GATES MAY BE EXPRESSED.** The sidebar and the page must
 * both derive visibility from this one call — a second copy of the boolean expression is how a
 * section ends up in the nav but not on the page.
 *
 * Mirrors the gates `Dashboard.tsx` applied inline before this module existed:
 *   - `isPeptideOnly` hides supplements, reports and appointments;
 *   - `!isAppointmentAvailable` hides appointments.
 */
export function visiblePatientSections(opts: {
	isPeptideOnly: boolean;
	isAppointmentAvailable: boolean;
}): PatientDashboardSection[] {
	const { isPeptideOnly, isAppointmentAvailable } = opts;
	return PATIENT_DASHBOARD_SECTIONS.filter((section) => {
		if (isPeptideOnly && (section.key === "supplements" || section.key === "reports" || section.key === "appointments")) return false;
		if (!isAppointmentAvailable && section.key === "appointments") return false;
		return true;
	});
}
